import React from 'react'
import { useFormik } from 'formik'
import { BrandConfig, BrandConfigResponse } from '../../core/_models'
import { updateBrandImagemLateral, updateBrandLayout, updateBrandLogo } from '../../core/_requests'
import { brandEditSchema } from './validation/brandEditSchema'
import { useModal } from 'app/components/Modal/ModalContext'
import { Button, FormFileInput, Label, LoadingIndicator } from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'
import { ButtonType, showError } from '_metronic/helpers'
import { getCSSVariableValue } from '_metronic/assets/ts/_utils'

interface BrandEditFormValues {
  primaryColor: string
  primaryActiveColor: string
  asideColor: string
  logo: File | null
  imagemLateral: File | null
}

interface BrandEditModalProps {
  brand?: BrandConfigResponse
}

export const BrandEditModal: React.FC<BrandEditModalProps> = ({ brand }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const formik = useFormik<BrandEditFormValues>({
    initialValues: {
      primaryColor: brand?.layout?.primaryColor || getCSSVariableValue('--brand-primary'),
      primaryActiveColor:
        brand?.layout?.primaryActiveColor || getCSSVariableValue('--brand-primary-dark'),
      asideColor: brand?.layout?.asideColor || getCSSVariableValue('--bs-aside-bg-color'),
      logo: null,
      imagemLateral: null,
    },
    validationSchema: brandEditSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true)

        const layoutPayload: BrandConfig = {
          primaryColor: values.primaryColor,
          primaryActiveColor: values.primaryActiveColor,
          asideColor: values.asideColor,
        }

        await updateBrandLayout(Number(brand?.id), layoutPayload)

        if (values.logo) await updateBrandLogo(Number(brand?.id), values.logo)
        if (values.imagemLateral)
          await updateBrandImagemLateral(Number(brand?.id), values.imagemLateral)

        window.dispatchEvent(new CustomEvent('brandDataRefresh'))
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const renderColorInput = (fieldName: keyof BrandEditFormValues, label: string) => {
    const value = formik.values[fieldName] as string
    const error = formik.touched[fieldName] ? formik.errors[fieldName] : null

    return (
      <div className='mb-3'>
        <Label className='mb-2'>{label}</Label>
        <div className='input-group'>
          <input
            type='text'
            id={fieldName}
            className={`form-control ${error ? 'is-invalid' : ''}`}
            value={value}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='#000000'
            disabled={formik.isSubmitting}
          />
          <input
            type='color'
            className='form-control form-control-color'
            style={{ width: '50px', cursor: 'pointer' }}
            value={value}
            onChange={(e) => formik.setFieldValue(fieldName, e.target.value)}
            disabled={formik.isSubmitting}
          />
        </div>
        {error && <div className='invalid-feedback d-block'>{error}</div>}
      </div>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='row'>
        <div className='col-md-6 mb-4'>{renderColorInput('primaryColor', 'Cor primária')}</div>
        <div className='col-md-6 mb-4'>
          {renderColorInput('primaryActiveColor', 'Cor primária ativa')}
        </div>
        <div className='col-md-6 mb-4'>{renderColorInput('asideColor', 'Cor menu lateral')}</div>
      </div>
      <div className='row'>
        <div className='col-md-6 mb-4'>
          <FormFileInput
            formik={formik}
            name='logo'
            entity='logo'
            accept='image/*'
            isDisabled={formik.isSubmitting}
          />
        </div>
        <div className='col-md-6 mb-4'>
          <FormFileInput
            formik={formik}
            name='imagemLateral'
            entity='imagem_lateral_login'
            accept='image/*'
            isDisabled={formik.isSubmitting}
          />
        </div>
      </div>

      <div className='d-flex justify-content-center mt-5'>
        <Button
          type={ButtonType.Reset}
          className='btn-secondary me-3'
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
